/**
 * books-adapter.ts — 真实后端 books DTO → 前端 mock BookRow / ChapterRow 塑形
 *
 * 接真改造：页面继续消费 mock 富集行类型（BookRow / ChapterRow），本文件把
 * snake_case 规范化 DTO 塑形成 camelCase 富集行，页面与设计还原 100% 不动。
 *
 * per-locale 建模（dev-docs PR #62/#76 ADR-005）：一本书 = 一个书组（NovelGroupSummary），
 * 每个语言版本是 novels 表的独立一行，文案字段（title / cover_url / author_display）per-locale。
 * 列表一行 = 一个书组聚合（不再每行一个 locale 重复出现）。
 *
 * 真 API 缺 mock 的若干聚合 / 关联名字段，处理策略：
 *  - 关联名（category 名 / tags 名）由调用方查 categories / tags 后经 opts 注入
 *  - 聚合指标（readers / payers / paidChapters）经 opts.stats，无则 0
 *  - displayTitle = 当前展示那一行的 per-locale 书名，无代表行时回退书组 cn_title
 *    （中文运营识别名，跨 locale 稳定）
 *  - coverHue：DTO 无此视觉字段，由 group_id 确定性派生
 */

import type {
  BookListQuery,
  ChapterResponse,
  NovelGroupSummary,
  NovelVisibility,
} from '@novel-admin/shared';
import type { BookRow, ChapterRow } from '~/data/mock/types';
import { visibilityDisplayOf } from '~/lib/novel-visibility-display';

/** 连载状态（ONGOING/COMPLETED/HIATUS）→ mock 中文连载标签 */
const SERIAL_STATUS_LABEL: Record<string, string> = {
  ONGOING: '连载中',
  COMPLETED: '已完结',
  HIATUS: '停更',
};

/** 篇幅（ADM-BOK-S019 length_class）→ 列表中文展示标签；未标注（null）不展示 */
const LENGTH_CLASS_LABEL: Record<string, string> = {
  SHORT: '短篇',
  MEDIUM: '中篇',
  MEDIUM_LONG: '中长篇',
  LONG: '长篇',
  EPIC: '超长篇',
};

/** 来源（ORIGINAL/LICENSED/IMPORTED）→ mock 来源展示标签 */
const SOURCE_LABEL: Record<NovelGroupSummary['source'], string> = {
  ORIGINAL: '原创',
  LICENSED: '授权',
  IMPORTED: '导入',
};

/**
 * coverHue — 由 group_id 确定性派生封面色相（0~359）。
 * DTO 无视觉字段；用 id 各字符 charCode 累加取模，保证同一书每次渲染色相稳定。
 */
function hueFromId(id: string): number {
  let acc = 0;
  for (let i = 0; i < id.length; i++) {
    acc = (acc + id.charCodeAt(i) * 31) % 360;
  }
  return acc;
}

/** adaptNovelToBookRow 的可选注入项：关联名 + 聚合统计（书组列表 DTO 不带） */
export interface AdaptNovelOptions {
  /**
   * 服务端 per-locale 筛选命中的明确成员；省略时回退 root member。
   * null = 旧 Worker 返回了跨 sibling 的假阳性，页面可只读展示，但不得暴露行级写目标。
   */
  displayLanguage?: NovelGroupSummary['languages'][number] | null;
  /** 分类显示名：调用方用明确展示成员的 category_id 查 categories 后注入；缺则空串 */
  categoryName?: string;
  /** 标签显示名数组：调用方用 group.tags（id[]）查 tags 后注入；缺则 [] */
  tagNames?: string[];
  /** 聚合统计（readers/payers/paidChapters）；缺则全 0 */
  stats?: { reading_users?: number; paying_users?: number; paid_chapters?: number };
}

/**
 * Web-first 滚动发布期间，仅把旧组级字段补到其真实归属的成员 —— 那批兼容标量取自**书组代表行**
 * （dd#970 后服务端也按代表行填：`root_novel_id` 所指行，可空时回退 locale 升序第一个）。
 * 判据要与服务端同源，否则会把书组标量补到错误的语言行上。
 */
function withLegacySummaryFallback(
  group: NovelGroupSummary,
  member: NovelGroupSummary['languages'][number],
  representativeNovelId: string | null,
): NovelGroupSummary['languages'][number] {
  const isRepresentative = member.novel_id === representativeNovelId;
  return {
    ...member,
    cover_url: member.cover_url ?? (isRepresentative ? group.cover_url : ''),
    age_rating: member.age_rating ?? (isRepresentative ? group.age_rating : undefined),
    category_id: member.category_id ?? (isRepresentative ? group.category_id : ''),
    tags: member.tags ?? (isRepresentative ? group.tags : []),
    total_chapters: member.total_chapters ?? (isRepresentative ? group.total_chapters : 0),
  } as NovelGroupSummary['languages'][number];
}

/**
 * 书组代表行（A6 §15.1）：`root_novel_id` 所指的 active 成员；该列可空（dd#923 语义 6）或
 * 所指行不在成员集内时回退 `locale` 升序第一个。服务端与 Web 用同一口径求值。
 */
function representativeNovelId(group: NovelGroupSummary): string | null {
  const sorted = [...group.languages].sort((a, b) => (a.locale < b.locale ? -1 : 1));
  const byRoot = group.root_novel_id
    ? sorted.find((member) => member.novel_id === group.root_novel_id)
    : undefined;
  return (byRoot ?? sorted[0])?.novel_id ?? null;
}

/**
 * adaptNovelToBookRow — NovelGroupSummary（一个书组）→ BookRow。
 *
 * 字段映射（书组 DTO → BookRow）：
 *  id              ← group_id（书组身份，dd#970；不再是「书组根 novel id」）
 *  displayTitle    ← 明确选中的 root member title；缺代表行时回退 cn_title
 *  originalTitle   ← 书组 original_title（原作书名，常是外文，不冒充 locale title）
 *  author          ← 明确选中 member 的 author_display；缺则 original_author
 *  coverUrl        ← 明确选中的 root member cover_url
 *  coverHue        ← hueFromId(group_id)（派生）
 *  category        ← opts.categoryName（查 category_id）；缺则 ''
 *  tags            ← opts.tagNames（查 tags id[]）；缺则 []
 *  langs           ← languages 各行 locale
 *  chapters        ← 明确选中 member 的 total_chapters
 *  publishedChapters ← 同一 member 的 total_chapters（DTO 无已发布计数）
 *  status          ← 明确选中的 root member status 标签
 *  serial          ← 同上取 serial_status
 *  freeChapters    ← 0（书组级无单一免费章节数，per-locale 独立；详情页按语言展示）
 *  readers/payers/paidChapters ← opts.stats（缺则 0）
 *  source          ← SOURCE_LABEL[source]
 *  createdAt       ← created_at（root 创建时刻）
 *  updatedAt       ← updated_at（全部 active members 的 MAX）
 */
export function adaptNovelToBookRow(
  group: NovelGroupSummary,
  opts: AdaptNovelOptions = {},
): BookRow {
  const stats = opts.stats;
  const selectionWasProvided = Object.hasOwn(opts, 'displayLanguage');
  const selected = selectionWasProvided
    ? opts.displayLanguage
    : selectSummaryDisplayLanguage(group);
  const representativeId = representativeNovelId(group);
  const fallbackLanguages = group.languages.map((member) =>
    withLegacySummaryFallback(group, member, representativeId),
  );
  // 旧 Worker 可能让多个 member 分别命中不同筛选维度。此时仍用代表行做只读占位展示，
  // 但 displayNovelId 留空，确保列表动作不能误写到不该写的行。
  const repr =
    selected ??
    fallbackLanguages.find((member) => member.novel_id === representativeId) ??
    fallbackLanguages[0];
  return {
    // 无可信写目标时 id 退回书组身份（仅作 React key / 折叠态键，不作 per-locale 写目标）
    id: selected === null ? group.group_id : (repr?.novel_id ?? group.group_id),
    displayNovelId: selected === null ? '' : (repr?.novel_id ?? ''),
    groupId: group.group_id,
    bookGroupCode: group.book_group_code ?? null,
    languageCodes: group.languages.map((l) => ({
      locale: l.locale,
      bookCode: l.book_code ?? null,
      novelId: l.novel_id,
      title: l.title,
      // 合成受众档（层② 覆盖层①）+ 覆盖来源；判据走 display 模块，不在此内联
      visibility: visibilityDisplayOf(l.visibility, l.group_default_visibility).value,
      visibilityOverridden: l.visibility !== null,
      serial: SERIAL_STATUS_LABEL[l.serial_status] ?? l.serial_status,
      isRecommended: l.is_recommended,
      chapters: l.total_chapters,
      publishedChapters: l.total_chapters,
      coverUrl: l.cover_url ?? '',
      ageRating: l.age_rating ?? null,
      authorDisplay: l.author_display ?? null,
      freeChapterCount: l.free_chapter_count ?? null,
      costPerKw: l.cost_default_per_kw ?? null,
      totalWords: l.total_words ?? null,
      lengthClass: l.length_class ? (LENGTH_CLASS_LABEL[l.length_class] ?? l.length_class) : null,
      publishedAt: l.published_at ?? null,
      source: l.source ? (SOURCE_LABEL[l.source] ?? l.source) : null,
      sourceProvider: l.source_provider ?? null,
      description: l.description ?? null,
      // S024 有效标签由服务端按 §15.3 口径解析（成员跟随译制基准行），此处原样透传；旧 Worker 无该键 → null
      editionLabel: l.edition_label ?? null,
    })),
    // `displayTitle` = **当前展示那一行**的 per-locale 书名（切语言就变），与列表「展示哪一行就操作哪一行」一致。
    // 书组级的中文运营识别名走下面的 `groupTitleZh`，两者不可互相顶替：活跃书组的代表行必有 `title`，
    // 若把 cn_title 写成这里的回退分支，它就永远轮不到展示（dd#970 的新字段等于白填）。
    displayTitle: repr?.title ?? group.cn_title,
    originalTitle: group.original_title,
    // 书组中文运营识别名（dd#970 §13.2）：组头按它展示，与「书组下有没有中文语言版」彻底脱钩。
    // 旧 Worker 的 DTO 无此键 → 落空串，由组头自己回退到原作书名。
    groupTitleZh: group.cn_title ?? '',
    author: repr?.author_display ?? group.original_author ?? '',
    coverUrl: repr?.cover_url ?? '',
    coverHue: hueFromId(group.group_id),
    category: opts.categoryName ?? '',
    tags: opts.tagNames ?? [],
    langs: group.languages.map((l) => l.locale),
    displayLocale: repr?.locale ?? '',
    chapters: repr?.total_chapters ?? 0,
    publishedChapters: repr?.total_chapters ?? 0,
    // 代表行的合成受众档；无代表行（不可达：活跃书组必有成员）时回落书组默认层
    visibility: repr
      ? visibilityDisplayOf(repr.visibility, repr.group_default_visibility).value
      : ((group.languages[0]?.group_default_visibility ?? 'UNLOCKED_ONLY') as NovelVisibility),
    visibilityOverridden: repr ? repr.visibility !== null : false,
    // 书组默认层：DTO 只在 `languages[]` 逐项回显（§15.2 顶层那份在详情 DTO 上），故取代表行的那一份；
    // 同组各行同值，取哪一行都一样，代表行缺失时取第一行。
    groupDefaultVisibility:
      (repr ?? group.languages[0])?.group_default_visibility ?? 'UNLOCKED_ONLY',
    serial: repr ? (SERIAL_STATUS_LABEL[repr.serial_status] ?? repr.serial_status) : '',
    // per-locale 独立免费章节数，书组级无单一值；列表不展示精确值（详情页按语言行展示）
    freeChapters: 0,
    readers: stats?.reading_users ?? 0,
    payers: stats?.paying_users ?? 0,
    paidChapters: stats?.paid_chapters ?? 0,
    source: SOURCE_LABEL[group.source],
    createdAt: group.created_at,
    updatedAt: group.updated_at,
  };
}

/**
 * 优先采用新服务端按完整查询（含 q）返回的明确投影。旧 Worker 无该字段时，Web 在已有摘要字段
 * 范围内兼容选择：member 筛选要求同一行；q 可按 title 选择语言，无法可靠识别时返回 null 禁写。
 */
export function selectSummaryDisplayLanguage(group: NovelGroupSummary, filters?: BookListQuery) {
  const representativeId = representativeNovelId(group);
  const languages = group.languages.map((member) =>
    withLegacySummaryFallback(group, member, representativeId),
  );
  if (Object.hasOwn(group, 'display_novel_id')) {
    if (!group.display_novel_id) return null;
    return languages.find((member) => member.novel_id === group.display_novel_id) ?? null;
  }
  const hasMemberFilters = Boolean(
    filters?.category?.length ||
      filters?.tag?.length ||
      filters?.lang?.length ||
      filters?.visibility ||
      filters?.serial_status?.length ||
      filters?.is_recommended !== undefined,
  );
  if (hasMemberFilters) {
    const matched = languages.find(
      (member) =>
        (!filters?.category?.length || filters.category.includes(member.category_id)) &&
        (!filters?.tag?.length || filters.tag.some((tag) => member.tags.includes(tag))) &&
        (!filters?.lang?.length || filters.lang.includes(member.locale)) &&
        // 与 SQL `COALESCE(novels.visibility, group_default_visibility)` 逐字对应：
        // 这里比裸 `member.visibility` 会让「继承书组默认」的行被筛掉，而 SQL 侧命中，
        // 于是 display_novel_id 落 null —— 契约 §15.1 依赖两侧一致来保证 null 不出现在正常路径。
        (!filters?.visibility ||
          filters.visibility ===
            visibilityDisplayOf(member.visibility, member.group_default_visibility).value) &&
        (!filters?.serial_status?.length || filters.serial_status.includes(member.serial_status)) &&
        (filters?.is_recommended === undefined || member.is_recommended === filters.is_recommended),
    );
    if (matched) return matched;
    return null;
  }
  const representative =
    languages.find((member) => member.novel_id === representativeId) ?? languages[0];
  if (!filters?.q) return representative;

  const query = filters.q.toLocaleLowerCase();
  const includesQuery = (value: string | null | undefined) =>
    value?.toLocaleLowerCase().includes(query) ?? false;
  // 命中书组级身份（书组 id / 译制基准行 id / 书组编码 / 中文识别名 / 原作信息）→ 取代表行
  if (
    group.group_id.toLocaleLowerCase() === query ||
    group.root_novel_id?.toLocaleLowerCase() === query ||
    includesQuery(group.book_group_code) ||
    includesQuery(group.cn_title) ||
    includesQuery(group.original_title) ||
    includesQuery(group.original_author)
  ) {
    return representative;
  }
  return (
    languages.find(
      (member) =>
        includesQuery(member.title) ||
        includesQuery(member.author_display) ||
        includesQuery(member.book_code),
    ) ?? null
  );
}

/**
 * adaptChapter — ChapterResponse → ChapterRow。
 *
 * 字段映射（DTO → ChapterRow）：
 *  id          ← id
 *  no          ← chapter_index
 *  title       ← title
 *  words       ← word_count
 *  price       ← Number(cost)（cost 是 bigint string，章节价格不会溢出 number）
 *  isFree      ← is_free（免费标识由服务端持久化字段决定，不从 price 派生）
 *  status      ← 由 published_at 派生（发布计划/队列是书级状态，不混入章节状态）
 *  publishedAt ← published_at
 *  snapshotVersion / hasPendingDraft / baseSnapshotVersion ← 线上快照与待重发布草稿状态
 *  updatedAt        ← updated_at（编辑器顶栏「已保存 · 时刻」接真）
 *  lastEditedByName ← last_edited_by_name（编辑器正文 meta「最后修改 by X」接真；fanout/未编辑章节为 null）
 */
export function adaptChapter(c: ChapterResponse): ChapterRow {
  return {
    id: c.id,
    no: c.chapter_index,
    title: c.title,
    words: c.word_count,
    price: Number(c.cost),
    isFree: c.is_free,
    status: c.published_at === null ? '草稿' : '已发布',
    publishedAt: c.published_at,
    snapshotVersion: c.snapshot_version,
    hasPendingDraft: c.has_pending_draft,
    baseSnapshotVersion: c.base_snapshot_version,
    updatedAt: c.updated_at,
    lastEditedByName: c.last_edited_by_name,
  };
}
